import React from 'react';
// BrowserRouter so share URLs are crawlable (`/token/:mint`, `/groups/:mint`).
// Hash routing hides the path behind `#`, which Twitter/Discord/Telegram
// link-unfurlers ignore — they'd only ever see the bare domain. Cloudflare
// Pages serves index.html for any unknown path via _redirects, so SPA
// routing still works with BrowserRouter; per-token OG meta is then
// injected by the Pages Functions in `/functions/token/[mint].js` and
// `/functions/groups/[mint].js` before the HTML reaches the client.
import { Route, BrowserRouter as Router, Routes, useLocation, Navigate } from 'react-router-dom';
//  import './App.css';
import './AppNew.css';
import './styles/premium.css';
// Side-effect import: starts the module-level live-feed buffer that subscribes
// to slurpWs at app boot. Without this, LiveFeed would only catch trades that
// arrive while the dashboard route is mounted — anything that fires while the
// user is on a token page would be missed.
import './lib/liveFeedStore';

// React Query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Farcaster mini-app SDK — calls actions.ready() so the splash dismisses
// when slurp is embedded as a Frame. Stays even though we ripped out the
// Coinbase MiniKit + wagmi/reown EVM stack (Farcaster's SDK is wallet-
// agnostic and doesn't pull in the EVM bundle).
import { sdk as farcasterSdk } from '@farcaster/miniapp-sdk';

// Import theme context but don't use ThemeToggle in App.js
import { ThemeProvider } from './context/ThemeContext';
import { SearchProvider } from './context/SearchContext';

// Solana wallet stack
import SolanaWalletProvider from './components/premium/SolanaWalletProvider';

// Import your components
import SolanaDeployTokenPage from './components/SolanaDeployTokenPage';
import DevTools from './components/DevTools';
import Leaderboard from './components/Leaderboard';
import JackpotPage from './components/JackpotPage';
import TopBar from './components/TopBar';
import TokenDashboard from './components/TokenDashboard';
import PremiumDetail from './components/premium/PremiumDetail';
import ProfilePage from './components/premium/ProfilePage';
import HolderGroupPage from './components/premium/HolderGroupPage';
import HolderGroupThread from './components/premium/HolderGroupThread';
import HolderGroupsIndex from './components/premium/HolderGroupsIndex';
import ProfileGuard from './components/premium/ProfileGuard';
import WelcomeModal from './components/premium/WelcomeModal';
import DocsPage from './components/DocsPage';
import DevelopersPage from './components/DevelopersPage';
import AdminPage from './components/admin/AdminPage';
import { WebSocketProvider } from './context/WebSocketContext';
import SiteFooter from './components/SiteFooter';
import ErrorBoundary from './components/ErrorBoundary';

// One-time migration: legacy bookmarks of `slurp.gg/#/token/abc` (HashRouter)
// land on `/` under BrowserRouter and lose their path. Detect the hash on
// initial load and rewrite the URL to the non-hash equivalent before React
// mounts, so the user ends up on the right page. Stays small; just one
// replaceState call before render.
if (typeof window !== "undefined" && window.location.hash.startsWith("#/")) {
  const target = window.location.hash.slice(1);
  if (target && target !== "/") {
    window.history.replaceState(null, "", target);
  }
}

// ScrollToTop component definition
function ScrollToTop() {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    // Reset scroll position when the path changes
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null; // This component doesn't render anything
}

// Farcaster mini-app readiness — when slurp is embedded as a Frame, we
// must call sdk.actions.ready() to dismiss the splash screen. No-op in a
// regular browser. Replaces the dual-SDK Farcaster+Coinbase MiniKit
// component; we ripped out Coinbase MiniKit when stripping the EVM
// stack since slurp is Solana-only.
function MiniKitReady() {
  React.useEffect(() => {
    (async () => {
      try {
        if (farcasterSdk?.actions?.ready) {
          await farcasterSdk.actions.ready();
        }
      } catch (err) {
        // Silent — outside a Farcaster frame the SDK isn't initialized
        // and the call throws; that's expected in the regular browser.
      }
    })();
  }, []);
  return null;
}

// Create query client
const queryClient = new QueryClient();

// EVM stack (wagmi/reown/coinbase) was ripped out — slurp is Solana-only.
// Legacy components still call appKitInstance.open() to show a wallet
// picker; redirect that to the Solana wallet modal via a window event
// SolanaWalletProvider listens for. Kept as a tiny stub rather than
// surgically removed since multiple files import it.
export const appKitInstance = {
  open: () => {
    window.dispatchEvent(new Event('open-solana-wallet'));
  },
  close: () => {},
};

// Routes wrapped in an error boundary keyed on the path. A render crash in
// any page shows the recoverable fallback instead of white-screening the
// whole app, and navigating to another route remounts the boundary (fresh
// key) so the error clears without a hard reload.
function RoutedContent() {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      <Routes>
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/" element={<TokenDashboard />} />
        <Route path="/token/:contractAddress" element={<PremiumDetail />} />
        <Route path="/deploy-token" element={<SolanaDeployTokenPage />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/jackpot" element={<JackpotPage />} />
        <Route path="/portfolio" element={<ProfilePage />} />
        <Route path="/profile/:wallet" element={<ProfilePage />} />
        <Route path="/groups" element={<HolderGroupsIndex />} />
        <Route path="/groups/:mint" element={<HolderGroupPage />} />
        <Route path="/groups/:mint/thread/:id" element={<HolderGroupThread />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/slurp-ops" element={<AdminPage />} />
      </Routes>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <SolanaWalletProvider>
      <QueryClientProvider client={queryClient}>
        <WebSocketProvider>
          <SearchProvider>
            <ThemeProvider>
              <Router>
                <ScrollToTop />
                <MiniKitReady />
                <div className="App">
                  <TopBar />
                  <WelcomeModal />
                  <ProfileGuard />
                  <div className="content-container-new">
                    <RoutedContent />
                  </div>
                  <SiteFooter />
                </div>
              </Router>
            </ThemeProvider>
          </SearchProvider>
        </WebSocketProvider>
      </QueryClientProvider>
    </SolanaWalletProvider>
  );
}

export default App;